




















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';

import ToggleSwitchComponent from '../../../../platform/components/shared/toggle-switch.component.vue';

import { StatsService } from '@/platform/services/stats.service';
import { FILTERS_GETTER_TYPES } from '../../../../platform/store/filters/filters.getter.types';

import { StatsBucketValues } from '@empathybroker/tsclient-stats/dist/enums/stats-bucket-values';
import { StatsQueriesTypes } from '@empathybroker/tsclient-stats/dist/enums/stats-queries-types';
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum';
import {
  KeywordDistributionResponse
} from '@empathybroker/tsclient-stats/dist/models/keyworddistributionreport/keyworddistribution-response';
import { KeywordReportResponse } from '@empathybroker/tsclient-stats/dist/models/keywordreport/keywordreport-response';
import {
  TermDistributionReportResponse
} from '@empathybroker/tsclient-stats/dist/models/termdistributionreport/termdistributionreport-response';
import { TermSuccessReportResponse } from '@empathybroker/tsclient-stats/dist/models/termsuccessreport/termsuccessreport-response';

import { Keyword, Query, Visualization } from '@empathybroker/seasonal-keywords';

@Component({
  components: {
    ToggleSwitch: ToggleSwitchComponent
  },
  computed: {
    ...mapGetters({
      filtersSelectedValues: FILTERS_GETTER_TYPES.selectedValues,
      filtersAreValid: FILTERS_GETTER_TYPES.areValid
    })
  }
})
export default class SeasonalkeywordsWrapperComponent extends Vue {

  @Prop() private loading: boolean;

  private filtersAreValid: boolean;

  private searchQuery = '';
  private showNoResults = false;
  private searchResultsText = '';
  private searchInputDisabled = false;

  private visualization: Visualization;

  private topKeywords: Keyword[];
  private searchKeywords: Keyword[];

  private fromSearch: boolean;
  private keywordsRelatedQueriesLoading: number;

  @Watch('filtersSelectedValues', { deep: true })
  private onFiltersSelectedValuesChange(): void {
    this.visualization.setData({
      keywords: []
    }, true);
    this.searchQuery = '';

    this.fetchData();
  }

  private mounted(): void {
    const visualizationHtmlElement = document.getElementById('seasonalkeywords-wrapper__visualization');
    this.visualization = new Visualization(visualizationHtmlElement, {
      width: visualizationHtmlElement.getBoundingClientRect().width,
      height: visualizationHtmlElement.getBoundingClientRect().height
    });

    this.visualization.onKeywordSelect = (keyword: Keyword) => {
      this.startLoadingRelatedQueries();
      this.getTopTermsPerKeyword(keyword.terms);
    };

    this.fetchData();
  }

  private beforeDestroy(): void {
    this.visualization.destroyVisualization();
  }

  private onSearchChange(): void {
    this.displayLoadingLabel();
    this.searchKeywords = [];

    if (!this.searchQuery) {
      this.fromSearch = false;

      this.visualization.setData({
        keywords: this.topKeywords
      });
      this.displayTopKeywordLabel();
    } else {
      this.fromSearch = true;

      this.getTopKeywords(this.searchQuery);
    }
  }

  private fetchData(): void {
    if (!this.filtersAreValid) {
      return;
    }

    this.displayLoadingLabel();
    this.topKeywords = [];

    this.fromSearch = false;
    this.keywordsRelatedQueriesLoading = 0;
    this.getTopKeywords();
  }

  private getTopKeywords(keyword?: string) {
    StatsService.getStatsReport(StatsReportsIdsEnum.KEYWORD, {
      rows: 10,
      type: (this.showNoResults ? StatsQueriesTypes.QUERIES_WITHOUT_RESULTS : StatsQueriesTypes.ALL_QUERIES),
      bucket: StatsBucketValues.DAY,
      keyword: (keyword || '')
    }).then((keywordReportResponse: KeywordReportResponse) => {
      if (keywordReportResponse.data.length !== 0) {
        this.getKeywordsDistributions(keywordReportResponse);
      } else {
        this.displayWithoutResultsLabel();
      }
    }).catch(() => this.handlerErrors());
  }

  private getKeywordsDistributions(keywordReportResponse: KeywordReportResponse) {
    Promise.all(keywordReportResponse.data.map(keywordData =>
      StatsService.getStatsReport(StatsReportsIdsEnum.KEYWORD_DISTRIBUTION, {
        bucket: StatsBucketValues.DAY,
        keyword: keywordData.keyword
      })
    )).then((keywordDistributionReportResponses: KeywordDistributionResponse[]) => {
      if (!this.fromSearch) {
        this.topKeywords = this.calculateKeywordsWithoutRelatedQueries(keywordDistributionReportResponses);
        this.visualization.setData({
          keywords: this.topKeywords
        });
        this.displayTopKeywordLabel();
      } else {
        this.searchKeywords = this.calculateKeywordsWithoutRelatedQueries(keywordDistributionReportResponses);
        this.visualization.setData({
          keywords: this.searchKeywords
        });
        this.displaySearchKeywordLabel();
      }
    }).catch(() => this.handlerErrors());
  }

  private getTopTermsPerKeyword(keywordTerm: string) {
    StatsService.getStatsReport(StatsReportsIdsEnum.TERM_SUCCESS, {
      rows: 5,
      type: (this.showNoResults ? StatsQueriesTypes.QUERIES_WITHOUT_RESULTS : StatsQueriesTypes.ALL_QUERIES),
      terms: keywordTerm
    }).then((termSuccessReportResponse: TermSuccessReportResponse) =>
      this.getTermsDistributions(termSuccessReportResponse)
    ).catch(() => this.handlerErrors());
  }

  private getTermsDistributions(termSuccessReportResponse: TermSuccessReportResponse) {
    Promise.all(termSuccessReportResponse.data.map(term =>
      StatsService.getStatsReport(StatsReportsIdsEnum.DAILY_DIST_TERM_SUCESS, {
        terms: term.terms
      })
    )).then((termDistResponses: TermDistributionReportResponse[]) => {
      if (!this.fromSearch) {
        this.topKeywords
          .find((topKeyword: Keyword) => topKeyword.terms === termSuccessReportResponse.params.terms)
          .relatedQueries = this.calculateRelatedQueries(termDistResponses);
      } else {
        this.searchKeywords
          .find((searchKeyword: Keyword) => searchKeyword.terms === termSuccessReportResponse.params.terms)
          .relatedQueries = this.calculateRelatedQueries(termDistResponses);
      }

      this.endLoadingRelatedQueries();
      this.visualization.setData({
        keywords: (!this.fromSearch) ? this.topKeywords : this.searchKeywords
      });
    }).catch(() => this.handlerErrors());
  }

  private calculateKeywordsWithoutRelatedQueries(keywordDistributionReportResponses: KeywordDistributionResponse[]): Keyword[] {
    return keywordDistributionReportResponses.map(keywordDistribution =>
      this.decodeKeywordDistributionResponseData(keywordDistribution)
    );
  }

  private calculateRelatedQueries(termDailyDistResponses: TermDistributionReportResponse[]): Query[] {
    return termDailyDistResponses.map(termDailyDist =>
      this.decodeTermDistributionResponseData(termDailyDist)
    );
  }

  private decodeKeywordDistributionResponseData(keyDist: KeywordDistributionResponse): Keyword {
    return {
      terms: keyDist.params.keyword,
      queryCount: keyDist.data.queryCount,
      clickCount: keyDist.data.clickCount,
      successCount: keyDist.data.successCount,
      add2cartCount: keyDist.data.add2cartCount,
      noResultsCount: keyDist.data.noResultsCount,
      distribution: keyDist.data.distributionData.map(distItem => {
        return {
          datetime: distItem.datetime,
          queryCount: distItem.queryCount,
          clickCount: distItem.clickCount,
          successCount: distItem.findabilityCount,
          add2cartCount: distItem.add2cartCount,
          noResultsCount: distItem.noResultsCount
        };
      }),
      relatedQueries: undefined
    };
  }

  private decodeTermDistributionResponseData(termDist: TermDistributionReportResponse): Query {
    return {
      terms: termDist.params.terms,
      queryCount: termDist.data.queryCount,
      clickCount: termDist.data.clickCount,
      successCount: termDist.data.successCount,
      add2cartCount: termDist.data.add2cartCount,
      noResultsCount: termDist.data.noResultsCount,
      distribution: termDist.data.distributionData.map(distItem => {
        return {
          datetime: distItem.datetime,
          queryCount: distItem.queryCount,
          clickCount: distItem.clickCount,
          successCount: distItem.findabilityCount,
          add2cartCount: distItem.add2cartCount,
          noResultsCount: distItem.noResultsCount
        };
      })
    };
  }

  private displayLoadingLabel(): void {
    this.startLoadingMask();
    this.searchResultsText = 'Loading data..';
  }

  private displayTopKeywordLabel(): void {
    this.stopLoadingMask();
    this.searchResultsText = `Showing Top ${this.topKeywords.length} keywords`;
    if (this.showNoResults) {
      this.searchResultsText += ' w/o results';
    }
  }

  private displaySearchKeywordLabel(): void {
    this.stopLoadingMask();
    const searchKeywordsCount = this.searchKeywords.length;
    this.searchResultsText = `Showing ${searchKeywordsCount} search ${((searchKeywordsCount === 1) ? 'result' : 'results')}`;
  }

  private displayWithoutResultsLabel(): void {
    this.stopLoadingMask();
    this.searchResultsText = 'No matches';
  }

  private handlerErrors(): void {
    this.stopLoadingMask();
    this.searchResultsText = 'Error loading data';
  }

  private startLoadingMask(): void {
    this.searchInputDisabled = true;
    this.$emit('update:loading', true);
  }

  private stopLoadingMask(): void {
    this.searchInputDisabled = false;
    this.$emit('update:loading', false);
  }

  private startLoadingRelatedQueries(): void {
    this.keywordsRelatedQueriesLoading++;
    this.searchInputDisabled = true;
  }

  private endLoadingRelatedQueries(): void {
    this.keywordsRelatedQueriesLoading--;
    if (this.keywordsRelatedQueriesLoading === 0) {
      this.searchInputDisabled = false;
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/vue-loader/lib/selector.js?type=script&index=0!./src/insights/components/catalog/seasonalkeywords/seasonalkeywords-wrapper.component.vue